// src/pages/CustomerDashboard.js

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getCardBalance, getCardDetails } from '../api/CardAPI';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Skeleton,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AddCardOutlined,
  CreditCardOutlined,
  PaymentsOutlined,
  ArrowForwardOutlined,
  ArrowBackOutlined,
  DiamondOutlined,
} from '@mui/icons-material';
import CountUp from 'react-countup';
import { QRCodeCanvas } from 'qrcode.react';
// WalletStatus component removed - all functionality integrated into dashboard
import TopUpDialog from '../components/TopUpDialog';
import WalletsAPI from '../api/WalletsAPI';
import {
  AccountBalanceWallet,
  AccountBalanceWalletOutlined,
  AddCircleOutline,
  ContentCopy,
  ArrowUpward,
  HourglassEmpty,
} from '@mui/icons-material';
import { updateDoc, doc as firestoreDoc, arrayRemove, arrayUnion } from 'firebase/firestore';

// Floating Card Component - Simplified without 3D motion effects
function FloatingCard({ cardData, index, balance, last4 }) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);

  const isPending = cardData.san && cardData.san.startsWith('pending-');

  const cardColors = [
    { bg: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)', pattern: '⬢' },
    { bg: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', pattern: '◆' },
    { bg: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)', pattern: '◈' },
    { bg: 'linear-gradient(135deg, #3b82f6 0%, #93c5fd 100%)', pattern: '◉' },
  ];

  const color = cardColors[index % cardColors.length];

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        width: '100%',
        opacity: 0,
        animation: `fadeInUp 0.6s ease ${index * 0.1}s forwards`,
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(20px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        },
        '&:hover': {
          transform: 'scale(1.05) translateY(-5px)',
        },
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      }}
    >
      <Box
        onTouchStart={(e) => {
          setTouchStart({
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
            time: Date.now()
          });
        }}
        onTouchEnd={(e) => {
          if (!touchStart) return;
          
          const touchEndPoint = {
            x: e.changedTouches[0].clientX,
            y: e.changedTouches[0].clientY,
            time: Date.now()
          };
          
          const distance = Math.sqrt(
            Math.pow(touchEndPoint.x - touchStart.x, 2) + 
            Math.pow(touchEndPoint.y - touchStart.y, 2)
          );
          
          const timeDiff = touchEndPoint.time - touchStart.time;
          
          // Only register as click if movement is less than 10px and time is less than 300ms
          if (distance < 10 && timeDiff < 300) {
            navigate(`/card/${cardData.san}`);
          }
          
          setTouchStart(null);
        }}
        onClick={(e) => {
          // Only for desktop
          if ('ontouchstart' in window) return;
          e.stopPropagation();
          navigate(`/card/${cardData.san}`);
        }}
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 200, sm: 220, md: 240 },
          background: color.bg,
          borderRadius: { xs: '16px', sm: '20px', md: '24px' },
          cursor: 'pointer',
          overflow: 'hidden',
          boxShadow: isHovered 
            ? '0 20px 40px rgba(0,0,0,0.3), 0 0 40px rgba(59, 130, 246, 0.5)' 
            : '0 10px 30px rgba(0,0,0,0.2)',
          transform: 'translateZ(50px)',
          transition: 'box-shadow 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 30% 20%, rgba(255,255,255,0.3) 0%, transparent 50%)`,
            pointerEvents: 'none',
          },
          '&::after': {
            content: `"${color.pattern}"`,
            position: 'absolute',
            right: '-10%',
            bottom: '-20%',
            fontSize: { xs: '100px', sm: '140px', md: '180px' },
            opacity: 0.1,
            transform: 'rotate(-15deg)',
            fontWeight: 900,
          }
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
          {/* Card Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 'auto' }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {cardData.cardBrand || 'Premium Card'}
              </Typography>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mt: 0.5, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                {cardData.cardName || 'My Card'}
              </Typography>
            </Box>
            <motion.div
              animate={{ rotate: isHovered ? 360 : 0 }}
              transition={{ duration: 0.5 }}
            >
              <DiamondOutlined sx={{ color: 'white', fontSize: { xs: 24, sm: 28, md: 32 } }} />
            </motion.div>
          </Box>

          {/* Card Number */}
          <Box sx={{ my: { xs: 2, sm: 2.5, md: 3 } }}>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.4rem' },
                fontFamily: 'monospace',
                letterSpacing: { xs: '0.1em', sm: '0.15em', md: '0.2em' },
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {isPending ? 'Pending...' : `•••• •••• •••• ${last4}`}
            </Typography>
            {isPending && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <HourglassEmpty sx={{ color: 'white', fontSize: 16 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                  Card is being created
                </Typography>
              </Box>
            )}
          </Box>

          {/* Balance */}
          <Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
              Available Balance
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1.3rem', sm: '1.6rem', md: '2rem' } }}>
                {cardData.currency || 'USD'}&nbsp;
                <CountUp
                  start={0}
                  end={balance}
                  duration={2}
                  decimals={2}
                  separator=","
                />
              </Typography>
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                  >
                    <Chip
                      size="small"
                      label="View Details"
                      icon={<ArrowForwardOutlined sx={{ fontSize: { xs: 14, sm: 16 } }} />}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        fontWeight: 600,
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        height: { xs: 24, sm: 28 },
                        display: { xs: 'none', sm: 'inline-flex' },
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// Animated Stats Card
function AnimatedStatsCard({ icon, title, value, subtitle, color, delay = 0 }) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <Paper
        sx={{
          p: { xs: 2, sm: 2.5, md: 3 },
          height: '100%',
          backgroundColor: '#FFFFFF',
          borderRadius: { xs: '12px', sm: '16px', md: '20px' },
          border: '1px solid #E2E8F0',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: `0 10px 40px ${color}20`,
            borderColor: color,
            '& .stats-icon': {
              transform: 'rotate(360deg) scale(1.2)',
            }
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1.5, sm: 2 } }}>
            <Box
              className="stats-icon"
              sx={{
                width: { xs: 40, sm: 48, md: 56 },
                height: { xs: 40, sm: 48, md: 56 },
                borderRadius: { xs: '10px', sm: '12px', md: '16px' },
                background: `linear-gradient(135deg, ${color}20 0%, ${color}40 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: color,
                transition: 'all 0.5s ease',
              }}
            >
              {icon}
            </Box>
            <Chip
              size="small"
              label={subtitle}
              sx={{
                bgcolor: `${color}15`,
                color: color,
                fontWeight: 600,
                border: `1px solid ${color}30`,
                fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                height: { xs: 20, sm: 22, md: 24 },
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            />
          </Box>
          
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.5rem', md: '2.125rem' } }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } }}>
            {title}
          </Typography>
        </Box>
        
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-50%',
            right: '-30%',
            width: '150%',
            height: '150%',
            background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
            transform: 'rotate(45deg)',
          }}
        />
      </Paper>
    </motion.div>
  );
}


// Quick Action Component
function QuickAction({ icon, title, color, onClick }) {
  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Tooltip title={title} placement="top" arrow>
        <Paper
          onClick={onClick}
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: '20px',
            backgroundColor: '#FFFFFF',
            background: `linear-gradient(135deg, ${color}15 0%, ${color}30 100%)`,
            border: `1px solid ${color}40`,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 8px 20px ${color}30`,
              borderColor: color,
              '& .action-icon': {
                transform: 'rotate(360deg)',
              }
            }
          }}
        >
          <Box
            className="action-icon"
            sx={{
              color: color,
              transition: 'transform 0.5s ease',
              mb: 1,
            }}
          >
            {icon}
          </Box>
          <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
            {title}
          </Typography>
        </Paper>
      </Tooltip>
    </motion.div>
  );
}

export default function CustomerDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [cards, setCards] = useState([]);
  const [balances, setBalances] = useState({});
  const [last4Map, setLast4Map] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [topUpDialogOpen, setTopUpDialogOpen] = useState(false);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('');
  const [walletAddresses, setWalletAddresses] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Check and update pending cards
  const checkPendingCards = useCallback(async (cardsToCheck) => {
    const pendingCards = cardsToCheck.filter(card => 
      card.san && card.san.startsWith('pending-') && card.docid
    );
    
    if (pendingCards.length === 0) return;
    
    for (const pendingCard of pendingCards) {
      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? 'https://us-central1-cardsx-a303a.cloudfunctions.net/walletApi/card/update-pending'
          : 'https://pay-with-usdt.com/api/card/update-pending';
        
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ docid: pendingCard.docid })
        });
        
        if (response.ok) {
          const result = await response.json();
          if (result.status === 'EXECUTED' && result.san) {
            // Update the card in Firebase with the real SAN
            const userDocRef = firestoreDoc(db, 'users', currentUser.uid);
            
            // Remove old pending card
            await updateDoc(userDocRef, {
              cards: arrayRemove(pendingCard)
            });
            
            // Add updated card with real SAN
            const updatedCard = {
              ...pendingCard,
              san: result.san,
              status: 'active',
              cardData: result.cardData
            };
            delete updatedCard.docid; // Remove docid as it's no longer needed
            
            await updateDoc(userDocRef, {
              cards: arrayUnion(updatedCard)
            });
            
            // Update local state
            setCards(prevCards => 
              prevCards.map(card => 
                card.docid === pendingCard.docid ? updatedCard : card
              )
            );
          }
        }
      } catch (error) {
      }
    }
  }, [currentUser]);

  const fetchUserCards = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const userCards = data.cards || [];
        setCards(userCards);
        
        // Check for pending cards
        checkPendingCards(userCards);
      }
    } catch (err) {
      // Error logging removed for security
    } finally {
      setLoading(false);
    }
  }, [currentUser, checkPendingCards]);

  // Fetch wallet data
  const fetchWalletData = useCallback(async () => {
    if (!currentUser?.email) return;
    try {
      const data = await WalletsAPI.getUserWalletStatus(currentUser.email);
      setWalletData(data);
      // Also set wallet addresses for deposit dialog
      if (data?.receiveAddresses) {
        setWalletAddresses(data.receiveAddresses);
      }
    } catch (err) {
    }
  }, [currentUser]);

  // Fetch recent activity
  const fetchRecentActivity = useCallback(async () => {
    if (!currentUser?.email) return;
    try {
      setLoadingActivity(true);
      
      let activities = [];
      
      // Try to get top-up requests
      try {
        const topUps = await WalletsAPI.getTopUpRequests(currentUser.email);
        const formattedTopUps = topUps.map(topUp => ({
          ...topUp,
          type: 'topup',
          displayType: 'Card Top-up',
          icon: '💳',
          san: topUp.san,
          cardName: 'Card'
        }));
        activities = [...activities, ...formattedTopUps];
      } catch (err) {
        // No top-ups found
      }
      
      // Try to get transaction history
      try {
        const transactions = await WalletsAPI.getTransactionHistory(currentUser.email);
        // Filter out 'sent' transactions and only show 'incoming' ones
        const formattedTransactions = transactions
          .filter(tx => tx.type === 'incoming')
          .map(tx => ({
            id: tx.id || tx.txHash,
            type: tx.type || 'transfer',
            displayType: 'Received',
            icon: '📥',
            amount: tx.amount,
            currency: tx.currency || 'USDT',
            status: tx.status || 'completed',
            createdAt: tx.timestamp || tx.createdAt,
            description: 'Deposit received'
          }));
        activities = [...activities, ...formattedTransactions];
      } catch (err) {
        // No transactions found
      }
      
      // If no activities, show a placeholder
      if (activities.length === 0) {
        activities = [{
          id: 'welcome',
          type: 'info',
          displayType: 'Welcome',
          icon: '👋',
          amount: 0,
          status: 'info',
          createdAt: new Date().toISOString(),
          description: 'Start by creating a card or making a deposit'
        }];
      }
      
      // Sort by date, newest first
      activities.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setRecentActivity(activities.slice(0, 5)); // Show only last 5
    } catch (err) {
      // Error logging removed for security
      setRecentActivity([{
        id: 'error',
        type: 'info',
        displayType: 'No Activity',
        icon: '📋',
        amount: 0,
        status: 'info',
        createdAt: new Date().toISOString(),
        description: 'No recent activity to display'
      }]);
    } finally {
      setLoadingActivity(false);
    }
  }, [currentUser]);

  const loadCardData = useCallback(async (cardObj) => {
    // Skip loading data for pending cards
    if (cardObj.san && cardObj.san.startsWith('pending-')) {
      setBalances(prev => ({ ...prev, [cardObj.san]: 0 }));
      setLast4Map(prev => ({ ...prev, [cardObj.san]: 'XXXX' }));
      return;
    }
    
    try {
      const balance = await getCardBalance(cardObj.san);
      const details = await getCardDetails(cardObj.san);
      const last4 = details.cardNo ? details.cardNo.slice(-4) : '????';
      
      setBalances(prev => ({ ...prev, [cardObj.san]: balance }));
      setLast4Map(prev => ({ ...prev, [cardObj.san]: last4 }));
    } catch (err) {
      // Error logging removed for security
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchUserCards();
  }, [fetchUserCards]);
  
  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);
  
  useEffect(() => {
    fetchRecentActivity();
  }, [fetchRecentActivity]);

  useEffect(() => {
    cards.forEach(card => {
      if (!balances[card.san]) {
        loadCardData(card);
      }
    });
  }, [cards, balances, loadCardData]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Refresh all data silently
      fetchWalletData();
      fetchRecentActivity();
      cards.forEach(card => loadCardData(card));
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [cards, fetchWalletData, fetchRecentActivity, loadCardData]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Refresh data when user returns to tab
        fetchWalletData();
        fetchRecentActivity();
        cards.forEach(card => loadCardData(card));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Refresh on window focus
    const handleFocus = () => {
      fetchWalletData();
      fetchRecentActivity();
      cards.forEach(card => loadCardData(card));
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [cards, fetchWalletData, fetchRecentActivity, loadCardData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWalletData();
    for (const card of cards) {
      await loadCardData(card);
    }
    setRefreshing(false);
  };


  const totalBalance = Object.values(balances).reduce((sum, balance) => sum + balance, 0);
  
  // Handle TopUp success
  const handleTopUpSuccess = () => {
    setTopUpDialogOpen(false);
    handleRefresh(); // Refresh all data
  };

  // Get safe wallet balances
  const safeWalletBalances = {
    available: walletData?.balances?.available ?? 0,
    unconfirmed: walletData?.balances?.unconfirmed ?? 0,
    totalDeposits: walletData?.balances?.totalDeposits ?? 0,
    totalTopUp: walletData?.balances?.totalTopUp ?? 0,
  };

  // Handle copy address
  const handleCopyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  // Get selected wallet address
  const getSelectedWalletAddress = () => {
    if (!selectedNetwork || !walletAddresses.length) return null;
    
    // Network mapping: ETH = chainId 1 or 11155111 (Sepolia), TRC = chainId for Tron
    const chainIdMap = {
      'ETH': ['1', '11155111'], // Ethereum mainnet and Sepolia testnet
      'TRC': ['tron'] // Tron
    };
    
    const address = walletAddresses.find(addr => {
      if (selectedNetwork === 'ETH') {
        return chainIdMap.ETH.includes(addr.chainId);
      } else if (selectedNetwork === 'TRC') {
        return addr.chainId === 'tron' || addr.address?.address?.startsWith('T');
      }
      return false;
    });
    
    return address;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pt: 12 }}>
        <Container maxWidth="xl">
          <Grid container spacing={3}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} lg={3} key={i}>
                <Skeleton variant="rounded" height={140} sx={{ bgcolor: '#F1F5F9', borderRadius: '20px' }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#F8FAFC',
      background: 'linear-gradient(to bottom, #F8FAFC, #F1F5F9)',
      position: 'relative', 
      overflow: 'hidden' 
    }}>
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
            animation: 'float 20s infinite ease-in-out',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.08) 0%, transparent 50%)',
            animation: 'float 25s infinite ease-in-out reverse',
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
            '33%': { transform: 'translate(30px, -30px) rotate(120deg)' },
            '66%': { transform: 'translate(-20px, 20px) rotate(240deg)' },
          },
        }}
      />

      <Container maxWidth="xl" sx={{ py: 4, pt: 12, position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #1E293B 0%, #3B82F6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 18 ? 'Afternoon' : 'Evening'}, {currentUser?.displayName?.split(' ')[0] || 'there'}!
            </Typography>
            <Typography variant="h5" sx={{ color: '#64748B', fontWeight: 300 }}>
              Your financial command center awaits
            </Typography>
          </Box>
        </motion.div>

        {/* Stats Overview - Real Wallet Data */}
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 5 }}>
          <Grid item xs={6} sm={6} md={6}>
            <AnimatedStatsCard
              icon={<AccountBalanceWalletOutlined sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />}
              title="Wallet Balance"
              value={`$${safeWalletBalances.available.toFixed(2)}`}
              subtitle="Available&nbsp;&nbsp;USDT"
              color="#1e3a8a"
              delay={0}
            />
          </Grid>
          <Grid item xs={6} sm={6} md={6}>
            <AnimatedStatsCard
              icon={<CreditCardOutlined sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />}
              title="Total in Cards"
              value={`$${totalBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={`${cards.length} cards`}
              color="#2563eb"
              delay={0.1}
            />
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Box sx={{ mb: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#1E293B' }}>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={3}>
                <QuickAction
                  icon={<AddCircleOutline sx={{ fontSize: 32 }} />}
                  title="Top Up Card"
                  color="#2563eb"
                  onClick={() => setTopUpDialogOpen(true)}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={3}>
                <QuickAction
                  icon={<AccountBalanceWallet sx={{ fontSize: 32 }} />}
                  title="Deposit"
                  color="#1e3a8a"
                  onClick={() => setDepositDialogOpen(true)}
                />
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Cards Section */}
        <Box sx={{ mb: 5, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
                Your Cards
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Swipe to manage your digital wallet
              </Typography>
            </Box>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                startIcon={<AddCardOutlined />}
                onClick={() => navigate('/add-card')}
                sx={{
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(30, 58, 138, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
                  },
                }}
              >
                Add New Card
              </Button>
            </motion.div>
          </Box>

          {/* Cards Grid */}
          {cards.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                sx={{
                  p: { xs: 4, sm: 6, md: 8 },
                  textAlign: 'center',
                  borderRadius: '24px',
                  background: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)',
                  border: '2px dashed #CBD5E1',
                }}
              >
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 10, 0],
                    scale: [1, 1.1, 1, 1.1, 1],
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                >
                  <CreditCardOutlined sx={{ fontSize: { xs: 60, sm: 70, md: 80 }, color: '#94A3B8', mb: 3 }} />
                </motion.div>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#1E293B', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                  No Cards Yet
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748B', mb: 4, px: { xs: 2, sm: 0 } }}>
                  Add your first card to unlock the full experience
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddCardOutlined />}
                  onClick={() => navigate('/add-card')}
                  sx={{
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                    borderRadius: '12px',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 1.5, sm: 2 },
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    boxShadow: '0 4px 15px rgba(30, 58, 138, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)',
                    },
                  }}
                >
                  Add Your First Card
                </Button>
              </Paper>
            </motion.div>
          ) : (
            <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ pt: 2 }}>
              {cards.map((card, index) => (
                <Grid item xs={12} sm={6} lg={4} key={card.san}>
                  <Box sx={{ 
                    pb: { xs: 2, sm: 3 }, 
                    overflow: 'visible',
                    pointerEvents: 'none',
                    '& > *': {
                      pointerEvents: 'auto'
                    }
                  }}>
                    <FloatingCard
                      cardData={card}
                      index={index}
                      balance={balances[card.san] || 0}
                      last4={last4Map[card.san] || '????'}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Recent Activity Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Paper sx={{ 
            p: 4, 
            borderRadius: '24px', 
            mb: 4,
            backgroundColor: '#FFFFFF',
            border: '1px solid #E2E8F0',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                Recent Wallet Activity
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Your latest transactions and top-ups
              </Typography>
            </Box>
            
            {loadingActivity ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : recentActivity.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                color: '#64748B'
              }}>
                <PaymentsOutlined sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                <Typography variant="body1">No recent activity</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Your transactions will appear here
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentActivity.slice(0, 10).map((activity, index) => (
                  <motion.div
                    key={activity.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: '#F1F5F9',
                        borderRadius: 2,
                        border: '1px solid #E2E8F0',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#3B82F6',
                          transform: 'translateX(4px)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)'
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: activity.status === 'completed' 
                              ? 'rgba(16, 185, 129, 0.1)'
                              : 'rgba(245, 158, 11, 0.1)',
                            color: activity.status === 'completed'
                              ? '#10B981'
                              : '#F59E0B'
                          }}
                        >
                          {activity.icon ? (
                            <span style={{ fontSize: 24 }}>{activity.icon}</span>
                          ) : activity.type === 'deposit' ? (
                            <AddCircleOutline />
                          ) : (
                            <ArrowUpward />
                          )}
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.displayType || (activity.type === 'deposit' ? 'Deposit' : 'Card Top-Up')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            {activity.description || new Date(activity.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ textAlign: 'right' }}>
                        {activity.amount > 0 && (
                          <Typography variant="body1" sx={{ fontWeight: 600, color: '#1E293B' }}>
                            ${activity.amount.toFixed(2)}&nbsp;&nbsp;{activity.currency || 'USDT'}
                          </Typography>
                        )}
                        {activity.status && (
                          <Chip
                            label={activity.status}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: activity.status === 'completed'
                                ? 'rgba(16, 185, 129, 0.2)'
                                : 'rgba(245, 158, 11, 0.2)',
                              color: activity.status === 'completed'
                                ? '#10B981'
                                : '#F59E0B',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                    </Paper>
                  </motion.div>
                ))}
              </Box>
            )}
          </Paper>
        </motion.div>

        {/* Wallet Status */}
        <Box id="wallet-status" sx={{ mt: 4 }}>
          {/* WalletStatus removed - all functionality integrated above */}
        </Box>
      </Container>

      {/* Loading Bar */}
      <AnimatePresence>
        {refreshing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}
          >
            <LinearProgress 
              sx={{ 
                height: 4,
                bgcolor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  background: 'linear-gradient(90deg, #1e3a8a, #2563eb, #3b82f6, #60a5fa)',
                  backgroundSize: '200% 100%',
                  animation: 'gradient 2s ease infinite',
                },
                '@keyframes gradient': {
                  '0%': { backgroundPosition: '0% 50%' },
                  '100%': { backgroundPosition: '200% 50%' },
                },
              }} 
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* TopUp Dialog */}
      <TopUpDialog
        open={topUpDialogOpen}
        onClose={() => setTopUpDialogOpen(false)}
        uid={currentUser?.email}
        email={currentUser?.email}
        firebaseUid={currentUser?.uid}
        onSuccess={handleTopUpSuccess}
      />

      {/* Deposit Dialog */}
      <Dialog
        open={depositDialogOpen}
        onClose={() => {
          setDepositDialogOpen(false);
          setSelectedNetwork('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#FFFFFF',
            backdropFilter: 'blur(20px)',
            border: '1px solid #E2E8F0',
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #E2E8F0',
          pb: 2
        }}>
          <motion.div
            animate={{ rotate: [0, 360] }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            <AccountBalanceWallet sx={{ color: '#3B82F6', fontSize: 28 }} />
          </motion.div>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            Deposit to Wallet
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 4, pb: 3 }}>
          <AnimatePresence mode="wait">
            {!selectedNetwork ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                key="network-selection"
              >
                <Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      fontWeight: 600,
                      textAlign: 'center'
                    }}
                  >
                    Choose Your Network
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 4,
                      color: '#64748B',
                      textAlign: 'center'
                    }}
                  >
                    Select the blockchain network for your deposit
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Paper
                          onClick={() => setSelectedNetwork('ETH')}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: '2px solid',
                            borderColor: selectedNetwork === 'ETH' ? '#3B82F6' : '#E2E8F0',
                            borderRadius: 2,
                            background: selectedNetwork === 'ETH' 
                              ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)'
                              : '#F1F5F9',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#3B82F6',
                              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #627EEA 0%, #3C4FE0 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto',
                              mb: 2,
                              boxShadow: '0 4px 20px rgba(98, 126, 234, 0.4)'
                            }}
                          >
                            <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                              Ξ
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Ethereum
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
                            ETH / USDT
                          </Typography>
                          <Chip 
                            label="ERC-20" 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(98, 126, 234, 0.2)',
                              color: '#627EEA',
                              fontWeight: 600
                            }} 
                          />
                        </Paper>
                      </motion.div>
                    </Grid>
                    
                    <Grid item xs={6}>
                      <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                        <Paper
                          onClick={() => setSelectedNetwork('TRC')}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            textAlign: 'center',
                            border: '2px solid',
                            borderColor: selectedNetwork === 'TRC' ? '#EF4444' : '#E2E8F0',
                            borderRadius: 2,
                            background: selectedNetwork === 'TRC' 
                              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)'
                              : '#F1F5F9',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#EF4444',
                              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              margin: '0 auto',
                              mb: 2,
                              boxShadow: '0 4px 20px rgba(255, 0, 0, 0.4)'
                            }}
                          >
                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                              T
                            </Typography>
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            Tron
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
                            TRX / USDT
                          </Typography>
                          <Chip 
                            label="TRC-20" 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(239, 68, 68, 0.2)',
                              color: '#EF4444',
                              fontWeight: 600
                            }} 
                          />
                        </Paper>
                      </motion.div>
                    </Grid>
                  </Grid>
                </Box>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                key="address-display"
              >
                <Box>
                  {(() => {
                    const walletAddress = getSelectedWalletAddress();
                    const address = walletAddress?.address?.address || '';
                    
                    if (!walletAddress) {
                      return (
                        <Alert severity="error" icon={false} sx={{ 
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                          border: '1px solid rgba(239, 68, 68, 0.3)'
                        }}>
                          No wallet address found for {selectedNetwork} network
                        </Alert>
                      );
                    }
                    
                    return (
                      <>
                        {/* Network Header */}
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", duration: 0.5 }}
                          >
                            <Box
                              sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: selectedNetwork === 'ETH' 
                                  ? 'linear-gradient(135deg, #627EEA 0%, #3C4FE0 100%)'
                                  : 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 2,
                                boxShadow: selectedNetwork === 'ETH'
                                  ? '0 8px 32px rgba(98, 126, 234, 0.4)'
                                  : '0 8px 32px rgba(255, 0, 0, 0.4)'
                              }}
                            >
                              <Typography variant="h3" sx={{ color: 'white', fontWeight: 700 }}>
                                {selectedNetwork === 'ETH' ? 'Ξ' : 'T'}
                              </Typography>
                            </Box>
                          </motion.div>
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                            {selectedNetwork === 'ETH' ? 'Ethereum' : 'Tron'} Network
                          </Typography>
                          <Chip 
                            label={selectedNetwork === 'ETH' ? 'ERC-20 USDT' : 'TRC-20 USDT'} 
                            sx={{ 
                              bgcolor: selectedNetwork === 'ETH' 
                                ? 'rgba(98, 126, 234, 0.2)'
                                : 'rgba(239, 68, 68, 0.2)',
                              color: selectedNetwork === 'ETH' ? '#627EEA' : '#EF4444',
                              fontWeight: 600
                            }} 
                          />
                        </Box>

                        {/* Warning Alert */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <Alert 
                            severity="warning" 
                            sx={{ 
                              mb: 3,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
                              border: '1px solid rgba(245, 158, 11, 0.3)',
                              '& .MuiAlert-icon': {
                                color: '#F59E0B'
                              }
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Important: Send only {selectedNetwork === 'ETH' ? 'ETH or ERC-20' : 'TRX or TRC-20'} tokens
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              Sending other assets may result in permanent loss
                            </Typography>
                          </Alert>
                        </motion.div>
                        
                        {/* QR Code */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.3, type: "spring" }}
                        >
                          <Paper sx={{ 
                            p: 4,
                            mb: 4,
                            textAlign: 'center',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 3,
                            border: '1px solid #E2E8F0',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: -2,
                              left: -2,
                              right: -2,
                              bottom: -2,
                              background: selectedNetwork === 'ETH'
                                ? 'linear-gradient(45deg, #627EEA, #3C4FE0, #627EEA)'
                                : 'linear-gradient(45deg, #FF0000, #CC0000, #FF0000)',
                              borderRadius: 3,
                              backgroundSize: '200% 200%',
                              animation: 'gradient-shift 3s ease infinite',
                              zIndex: -1,
                            },
                            '@keyframes gradient-shift': {
                              '0%': { backgroundPosition: '0% 50%' },
                              '50%': { backgroundPosition: '100% 50%' },
                              '100%': { backgroundPosition: '0% 50%' },
                            }
                          }}>
                            <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, display: 'inline-block' }}>
                              <QRCodeCanvas
                                value={address}
                                size={220}
                                level="H"
                                includeMargin={true}
                                fgColor={selectedNetwork === 'ETH' ? '#3C4FE0' : '#CC0000'}
                              />
                            </Box>
                            <Typography variant="caption" sx={{ mt: 2, display: 'block', color: '#64748B' }}>
                              Scan QR code to get address
                            </Typography>
                          </Paper>
                        </motion.div>
                        
                        {/* Address */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccountBalanceWallet sx={{ fontSize: 18 }} />
                              Deposit Address
                            </Typography>
                            <Paper sx={{ 
                              p: 2.5,
                              background: '#F1F5F9',
                              borderRadius: 2,
                              border: '1px solid #E2E8F0',
                              position: 'relative',
                              overflow: 'hidden',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: selectedNetwork === 'ETH' ? '#627EEA' : '#EF4444',
                                transform: 'translateY(-2px)',
                                boxShadow: selectedNetwork === 'ETH'
                                  ? '0 4px 20px rgba(98, 126, 234, 0.2)'
                                  : '0 4px 20px rgba(239, 68, 68, 0.2)'
                              }
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace',
                                    wordBreak: 'break-all',
                                    flex: 1,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    letterSpacing: 0.5
                                  }}
                                >
                                  {address}
                                </Typography>
                                <Tooltip title="Copy address" placement="top">
                                  <IconButton
                                    onClick={() => handleCopyAddress(address)}
                                    sx={{ 
                                      color: '#3B82F6',
                                      bgcolor: '#EFF6FF',
                                      '&:hover': {
                                        bgcolor: '#3B82F6',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s ease'
                                    }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Paper>
                          </Box>
                        </motion.div>
                        
                        {/* Network info */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Paper sx={{ 
                                p: 2.5,
                                height: '100%',
                                background: selectedNetwork === 'ETH'
                                  ? 'linear-gradient(135deg, rgba(98, 126, 234, 0.1) 0%, rgba(98, 126, 234, 0.05) 100%)'
                                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: selectedNetwork === 'ETH' 
                                  ? 'rgba(98, 126, 234, 0.3)'
                                  : 'rgba(239, 68, 68, 0.3)'
                              }}>
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  mb: 0.5,
                                  color: '#64748B',
                                  fontWeight: 500
                                }}>
                                  NETWORK
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 600,
                                  color: selectedNetwork === 'ETH' ? '#627EEA' : '#EF4444'
                                }}>
                                  {selectedNetwork === 'ETH' ? 'Ethereum' : 'Tron'}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Paper sx={{ 
                                p: 2.5,
                                height: '100%',
                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                                borderRadius: 2,
                                border: '1px solid rgba(16, 185, 129, 0.3)'
                              }}>
                                <Typography variant="caption" sx={{ 
                                  display: 'block',
                                  mb: 0.5,
                                  color: '#64748B',
                                  fontWeight: 500
                                }}>
                                  TOKEN STANDARD
                                </Typography>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 600,
                                  color: '#10B981'
                                }}>
                                  {selectedNetwork === 'ETH' ? 'ERC-20' : 'TRC-20'}
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>
                        </motion.div>

                        {/* Bottom Note */}
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}
                        >
                          <Box sx={{ 
                            mt: 3,
                            p: 2,
                            textAlign: 'center',
                            borderRadius: 2,
                            bgcolor: '#EFF6FF'
                          }}>
                            <Typography variant="caption" sx={{ color: '#64748B' }}>
                              Minimum deposit: 10 USDT • Network fee applies
                            </Typography>
                          </Box>
                        </motion.div>
                      </>
                    );
                  })()}
                </Box>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 3, 
          pt: 0,
          borderTop: '1px solid #E2E8F0',
          gap: 2
        }}>
          {selectedNetwork && (
            <Button 
              onClick={() => setSelectedNetwork('')}
              startIcon={<ArrowBackOutlined />}
              sx={{
                color: '#64748B',
                '&:hover': {
                  bgcolor: '#EFF6FF'
                }
              }}
            >
              Back
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button 
            onClick={() => {
              setDepositDialogOpen(false);
              setSelectedNetwork('');
            }}
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #3B82F6, #0EA5E9)',
              '&:hover': {
                background: 'linear-gradient(45deg, #2563EB, #38BDF8)',
              }
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy Success Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopySuccess(false)}>
          Address copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
}
